import { previewPathToUrl } from '@lib/network/preview';
import { libraryPreview } from '@stores/Library/LibraryPreview';
import { useEffect, useState } from 'react';

export function usePreviewToken(libraryId?: string) {
  // Init synchronously from cache to avoid null→token flash on remount
  const [token, setToken] = useState<string | null>(() =>
    libraryId ? libraryPreview.getCachedPreviewToken(libraryId) : null,
  );

  useEffect(() => {
    if (!libraryId) {
      setToken(null);
      return;
    }

    let current = true;

    // Show the cached token straight away to avoid flicker, but always go on to
    // ask for a valid one. `getValidPreviewToken` is the cache's only writer and
    // resolves without a fetch unless the token expires within a day, so this is
    // what makes its proactive refresh actually run: returning early on a cache
    // hit meant nothing triggered it, and a token could be ridden to its last
    // second across reloads. The HLS players re-read the cache per request, so
    // they pick the new token up without rebuilding.
    const cached = libraryPreview.getCachedPreviewToken(libraryId);
    if (cached) {
      setToken(cached);
    }

    libraryPreview
      .getValidPreviewToken(libraryId)
      .then((token) => {
        if (current) setToken(token);
      })
      .catch((error) => {
        console.error(`Preview token unavailable for library ${libraryId}:`, error);
        // Don't leave another library's token in state to authenticate this one's
        // requests; a still-valid cached token for this library is fine to keep.
        const usable = libraryPreview.getCachedPreviewToken(libraryId);
        if (current && !usable) setToken(null);
      });

    return () => {
      current = false;
    };
  }, [libraryId]);

  return token;
}

export function useMediaFilePreview(libraryId?: string, mediaId?: string, filePath?: string) {
  const token = usePreviewToken(libraryId);

  // Init URL synchronously when token is already available
  const [url, setUrl] = useState<string | null>(() => {
    if (!libraryId || !mediaId || !filePath || !token) return null;
    return previewPathToUrl(libraryId, mediaId, filePath);
  });

  useEffect(() => {
    if (!libraryId || !mediaId || !filePath || !token) {
      setUrl(null);
      return;
    }

    const baseUrl = previewPathToUrl(libraryId, mediaId, filePath);
    setUrl(baseUrl);
  }, [libraryId, mediaId, filePath, token]);

  return { url, token };
}

export const getAccessTokenFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('access_token');
  } catch {
    return null;
  }
};

export const getLibraryIdFromPreviewUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    // Preview URL format: /{libraryId}/{mediaId}/...
    // So libraryId is the first non-empty part after the domain
    const libraryIdIndex = pathParts.findIndex((part) => part && part !== '');
    return libraryIdIndex !== -1 ? pathParts[libraryIdIndex] : null;
  } catch {
    return null;
  }
};
